<template>
  <Modal
    content-class="full-height column no-wrap overflow-hidden"
    :modal-style="{ maxWidth: '860px', height: '640px' }"
    persistent
    @close="emit('close')"
  >
    <template #header>
      <div
        class="tw:flex tw:items-center tw:gap-2"
        data-testid="header"
      >
        <BaseButton
          v-if="step === 3"
          circle
          icon="chevron-left"
          icon-size="lg"
          new-design
          new-icon
          @click="backToStart"
        />
        <div class="tw:text-lg tw:font-bold">
          {{ $t(`manage_social_integrations.${platformLabel}_modal_title`) }}
        </div>
      </div>
    </template>

    <SocialIntegrationRefreshIntegrationList
      v-if="step === 0"
      v-model:selected-integrations="integrationsToSave"
      identifier="remoteId"
      :integration-type="props.type"
      :integrations="refreshedIntegrations"
      list-type="new"
      @selected-integrations-updated="setIntegrationsToSave"
    />

    <SocialIntegrationExistingList
      v-else-if="step === 1"
      ref="existingList"
      :type="props.type"
      @remove-integration="confirmRemoveIntegration"
      @update-integration-team="integrationTeamUpdated"
    >
      <template #confirmation>
        <Transition name="fade">
          <AlertMessage
            v-if="Boolean(alert.message)"
            :type="alert.type"
          >
            {{ alert.message }}
          </AlertMessage>
        </Transition>
      </template>
    </SocialIntegrationExistingList>

    <div
      v-else-if="step === 2"
      class="column fit flex-center"
    >
      <QSpinner size="50" />
      <div>{{ statusText }}</div>
    </div>

    <SocialIntegrationRefreshIntegrations
      v-if="step === 3"
      :refreshed-integrations="refreshedIntegrations"
      :type="props.type"
      @selected-integrations-updated="setIntegrationsToSave"
    />

    <template #buttons>
      <div
        v-if="step !== 2"
        class="tw:flex tw:flex-1 tw:justify-end"
      >
        <div>
          <BaseButton
            data-testid="cancel-button"
            :label="getLocaleText('global.cancel')"
            new-design
            @click="emit('close')"
          />
          <BaseButton
            color="primary"
            data-testid="confirm-button"
            :disabled="actionDisabled"
            :label="actionText"
            new-design
            @click="runAction"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { Notify } from "quasar";
import { type Ref, computed, onMounted, ref, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import { AlertMessage, BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import SocialIntegrationExistingList from "shared/components/integrations/SocialIntegrationExistingList.vue";
import SocialIntegrationRefreshIntegrationList from "shared/components/integrations/SocialIntegrationRefreshIntegrationList.vue";
import SocialIntegrationRefreshIntegrations from "shared/components/integrations/SocialIntegrationRefreshIntegrations.vue";
import {
  type SocialUserRecord,
  useSocialAuth,
} from "shared/composables/useSocialAuth";
import { useTransientAlert } from "shared/composables/useTransientAlert";
import {
  type SocialIntegration,
  PLATFORM_LABELS,
} from "shared/constants/social";
import ModalService from "shared/modals/ModalService";
import {
  type OrganisationSocialIntegration,
  FacebookSocialIntegration,
  InstagramSocialIntegration,
  LinkedinSocialIntegration,
  TwitterSocialIntegration,
} from "shared/resources";
import { useSocialIntegrationsStore } from "shared/stores/socialIntegrations";
import useUserStore from "shared/stores/user";

interface ManageSocialIntegrationsProps {
  type: keyof typeof PLATFORM_LABELS;
  integrationsToAssociate?: SocialIntegration[];
}

type IntegrationPlatforms = {
  [key in keyof typeof PLATFORM_LABELS]: PlatformAttributes;
};

interface PlatformAttributes {
  platform: "facebook" | "twitter" | "instagram" | "linkedin";
  ResourceClass:
    | typeof FacebookSocialIntegration
    | typeof TwitterSocialIntegration
    | typeof LinkedinSocialIntegration
    | typeof InstagramSocialIntegration;
}

const props = withDefaults(defineProps<ManageSocialIntegrationsProps>(), {
  integrationsToAssociate: () => [],
});

const userStore = useUserStore();
const { organisation } = storeToRefs(userStore);

const socialIntegrationsStore = useSocialIntegrationsStore();
const { integrations } = storeToRefs(socialIntegrationsStore);
const { loadIntegrations } = socialIntegrationsStore;

const { login, isLoggedIn, userExitedLoginProcess } = useSocialAuth(props.type);

const loading = ref(false);

const step = ref<0 | 1 | 2 | 3>(1);

const existingList = ref();

const platformLabel = PLATFORM_LABELS[props.type].toLowerCase();

const refreshedIntegrations = ref([]) as Ref<SocialIntegration[]>;

const integrationsToSave = ref([]) as Ref<SocialIntegration[]>;

const { alert, showSuccess, showWarning } = useTransientAlert();

const platformAttributes = ref<IntegrationPlatforms>({
  FacebookSocialIntegration: {
    platform: "facebook",
    ResourceClass: FacebookSocialIntegration,
  },
  InstagramSocialIntegration: {
    platform: "instagram",
    ResourceClass: InstagramSocialIntegration,
  },
  TwitterSocialIntegration: {
    platform: "twitter",
    ResourceClass: TwitterSocialIntegration,
  },
  LinkedinSocialIntegration: {
    platform: "linkedin",
    ResourceClass: LinkedinSocialIntegration,
  },
});

const statusText = computed<string>(() => {
  const suffix = (text: string) => `${text}...`;

  const base = "manage_social_integrations";
  const logIn = getLocaleText(`${base}.logging_in`);
  const getProfiles = getLocaleText(`${base}.getting_profiles`);
  const exit = getLocaleText(`${base}.user_exited_auth`);

  if (userExitedLoginProcess.value) {
    return suffix(exit);
  }

  return suffix(isLoggedIn.value ? getProfiles : logIn);
});

const integrationPlatform = computed<PlatformAttributes>(
  () => platformAttributes.value[props.type]
);

const deletedAllIntegrations = computed<boolean>(() =>
  Boolean(!existingList.value?.fetchedIntegrations?.length)
);

const integrationsLookup = computed<{
  [key: string]: SocialIntegration;
}>(() =>
  integrations.value.reduce(
    (obj, integration) =>
      Object.assign(obj, {
        [String(integration.remoteId)]: integration,
      }),
    {}
  )
);

const actionDisabled = computed<boolean>(
  () =>
    (step.value === 3 || step.value === 0) && !integrationsToSave.value.length
);

const actionText = computed<string>(() => {
  if (step.value === 1) {
    return deletedAllIntegrations.value
      ? getLocaleText("global.connect")
      : getLocaleText("manage_social_integrations.update_integrations");
  }

  return getLocaleText("global.save");
});

const emit = defineEmits<{
  close: [];
}>();

onMounted(() => {
  loadIntegrations();
});

function confirmRemoveIntegration(integration: OrganisationSocialIntegration) {
  ModalService.open("ConfirmDialogModal", {
    props: {
      type: "confirm",
      theme: "danger",
      title: getLocaleText("manage_social_integrations.remove_integration"),
      dialogMaxWidth: "350px",
      message: buildDeleteConfirmationMessage(integration),
      confirmLabel: getLocaleText("global.confirm"),
    },
    events: {
      confirm: () => {
        removeIntegration(integration);
      },
      close: () => ModalService.close("ConfirmDialogModal"),
    },
  });
}

function integrationTeamUpdated(): void {
  showSuccess(getLocaleText("manage_social_integrations.team_updated"));
}

function buildDeleteConfirmationMessage(
  integration: OrganisationSocialIntegration
): string {
  let baseMessage = getLocaleText(
    "manage_social_integrations.remove_monitoring_text"
  );

  if (integration.scheduledPosts) {
    baseMessage = baseMessage.concat(
      `<ul><li>${getLocaleText("manage_social_integrations.pending_scheduled_posts_text", { count: integration.scheduledPosts })}</li>`
    );

    if (!integration.draftPosts) {
      baseMessage = baseMessage.concat("</ul>");
    }
  }

  if (integration.draftPosts) {
    if (!integration.scheduledPosts) {
      baseMessage = baseMessage.concat("<ul>");
    }

    baseMessage = baseMessage.concat(
      `<li>${getLocaleText("manage_social_integrations.draft_scheduled_posts_text", { count: integration.draftPosts })}</li></ul>`
    );
  }

  baseMessage = baseMessage.concat(
    getLocaleText("manage_social_integrations.disconnect_account")
  );

  return baseMessage;
}

async function removeIntegration(
  integration: OrganisationSocialIntegration
): Promise<void> {
  try {
    await $streemApiV1.post(
      `organisation_social_integrations/${integration.id}/archive`
    );

    showSuccess(
      getLocaleText("manage_social_integrations.account_disconnected")
    );

    await existingList.value.reload();
  } catch (error) {
    ErrorDialog(
      getLocaleText("manage_social_integrations.error_deleting"),
      error
    );
  } finally {
    loadIntegrations();
  }
}

function backToStart(): void {
  step.value = 1;
}

async function runAction(): Promise<void> {
  if (step.value === 1) {
    // TODO handle reconnection https://app.asana.com/0/1208784277174963/1209777787057014/f
    step.value = 2;
    await connectPage();

    return;
  }

  await saveIntegrations();

  if (step.value === 0) {
    emit("close");

    Notify.create({
      message: getLocaleText(
        "social_engage_settings.account_saved_successfully",
        { count: integrationsToSave.value.length }
      ),
    });
  }

  backToStart();

  showSuccess(getLocaleText("manage_social_integrations.accounts_updated"));
}

function setIntegrationsToSave(selectedIntegrations: SocialIntegration[]) {
  integrationsToSave.value = selectedIntegrations;
}

async function connectPage(): Promise<void> {
  try {
    const socialUserRecords = await login();

    selectIntegrations(socialUserRecords);
  } catch (error) {
    if (error instanceof Error) {
      backToStart();

      showWarning(getLocaleText("manage_social_integrations.auth_issue"));
    }
  }
}

function selectIntegrations(options: SocialUserRecord[]) {
  refreshedIntegrations.value = [];

  options.forEach((socialRecord: SocialUserRecord) => {
    const existingIntegration =
      integrationsLookup.value[String(socialRecord.id)];

    const newIntegration = new integrationPlatform.value.ResourceClass({
      ...existingIntegration,
    });

    if (existingIntegration) {
      newIntegration.id = existingIntegration.id;
      newIntegration.isPersisted = true;
    }

    refreshedIntegrations.value.push(
      Object.assign(newIntegration, {
        isOutreachAccount: false,
        label: socialRecord.name,
        metadata: socialRecord.metadata,
        organisationId: organisation.value.id,
        permissions: socialRecord.permissions,
        remoteId: socialRecord.id,
        scheduledAt: "",
        socialPlatform: props.type,
        token: socialRecord.accessToken,
        tokenRefreshedAt: new Date().toISOString(),
      })
    );
  });

  step.value = 3;
}

async function saveIntegrations() {
  try {
    await Promise.all(
      integrationsToSave.value.map((integration) => integration.saveResource())
    );

    await loadIntegrations();
  } catch (error) {
    ErrorDialog(
      getLocaleText("manage_social_integrations.error_fetching"),
      error
    );
  } finally {
    loading.value = false;
  }
}

function resetToExistingListAndReportError(): void {
  if (userExitedLoginProcess.value) {
    backToStart();
    showWarning(getLocaleText("manage_social_integrations.auth_cancelled"));
  }
}

watch(() => userExitedLoginProcess.value, resetToExistingListAndReportError);

watch(
  () => props.integrationsToAssociate,
  (integrationsToAssociate) => {
    if (
      Array.isArray(integrationsToAssociate) &&
      integrationsToAssociate.length > 0
    ) {
      step.value = 0;
      refreshedIntegrations.value = integrationsToAssociate;

      return;
    }

    step.value = 1;
  },
  {
    deep: true,
    immediate: true,
  }
);

defineExpose({
  step,
  removeIntegration,
  integrationsToSave,
});
</script>

<style lang="scss" scoped>
@import "shared/css/utils/transitions/fade";
</style>
